import { formatLocalDateKey } from "../../utils/dateRangeUtils"
import {
  formatShortDateInTimeZone,
  isCalendarDayInRange,
} from "./timezone"
import {
  dashboardPlatformKeyFromDescription,
  isInstagramGraphDescription,
  socialNetworkTypeIdToDashboardPlatformKey,
} from "./socialNetworkCatalog"

/** ISO date param for GET …/posts (month year/month or custom start_date/end_date). */
export function formatTopPostsReportDate(date) {
  return formatLocalDateKey(date)
}

export function parseTopPostsDateParts(date) {
  const iso = formatTopPostsReportDate(date)
  if (!iso) return null
  const [year, month, day] = iso.split("-")
  if (!year || !month || !day) return null
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  }
}

/** Query for GET /report/clients/{id}/posts. */
export function buildClientPostsReportQuery({
  period,
  year,
  month,
  aggregationMode,
  startDate,
  endDate,
  socialNetwork,
  sortBy,
  limit = 50,
} = {}) {
  const params = {
    aggregation_mode: aggregationMode ?? "paid_and_organic",
  }
  if (period === "custom" && startDate && endDate) {
    params.start_date = startDate
    params.end_date = endDate
  } else {
    params.period = period ?? "month"
    if (year != null) params.year = String(year)
    if (month != null) params.month = String(month)
  }
  if (socialNetwork && socialNetwork !== "all") {
    params.social_network = socialNetwork === "instagram" ? "instagram_graph" : socialNetwork
  }
  if (sortBy) params.sort_by = sortBy
  if (limit != null) params.limit = String(limit)
  return new URLSearchParams(params).toString()
}

export function buildTopPostsFetchKey(clientId, startDate, endDate, sortDirection = "desc", rangeMode) {
  const range = rangeMode === "custom" ? "|custom" : ""
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}|${sortDirection ?? "desc"}${range}`
}

export function unwrapTopPostsPayload(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.content)) return data.content
  if (Array.isArray(data?.posts)) return data.posts
  if (Array.isArray(data?.items)) return data.items
  return []
}

export function resolveTopPosts(slice, socialNetworkTypes = []) {
  if (slice?.raw != null) {
    return normalizeTopPostsPayload(slice.raw, socialNetworkTypes)
  }
  return Array.isArray(slice?.posts) ? slice.posts : []
}

export function platformFromSocialNetworkTypeId(typeId, postLink, socialNetworkTypes = []) {
  return socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
}

export function platformFromPostLink(postLink) {
  if (!postLink || typeof postLink !== "string") return null
  const u = postLink.toLowerCase()
  if (u.includes("instagram.com")) return "instagram"
  if (u.includes("facebook.com")) return "facebook"
  if (u.includes("linkedin.com")) return "linkedin"
  if (u.includes("twitter.com") || u.includes("x.com")) return "twitter"
  if (u.includes("tiktok.com")) return "tiktok"
  return null
}

/** social_network from post performance API → dashboard platform key (`instagram_graph` only). */
export function platformFromSocialNetworkName(name) {
  return dashboardPlatformKeyFromDescription(name)
}

export function resolveTopPostPlatform(typeId, postLink, socialNetworkName, socialNetworkTypes = []) {
  return (
    platformFromSocialNetworkTypeId(typeId, postLink, socialNetworkTypes) ||
    platformFromSocialNetworkName(socialNetworkName) ||
    platformFromPostLink(postLink) ||
    null
  )
}

/** Dashboard key used for Post Performance icons and filters. */
export function topPostDashboardPlatformKey(post) {
  if (!post) return null
  const stored = String(post.platform || "").trim().toLowerCase()
  if (stored === "instagram" || stored === "facebook" || stored === "linkedin" || stored === "twitter" || stored === "tiktok") {
    return stored
  }
  if (stored === "instagram_graph") return "instagram"
  if (stored === "x") return "twitter"
  const fromNetwork =
    dashboardPlatformKeyFromDescription(post.socialNetwork) ||
    platformFromSocialNetworkName(post.socialNetwork)
  if (fromNetwork && fromNetwork !== "all") return fromNetwork
  const fromLink = platformFromPostLink(post.postLink || post.permalink)
  if (fromLink) return fromLink
  const fromStored = dashboardPlatformKeyFromDescription(post.platform)
  if (fromStored && fromStored !== "all") return fromStored
  return null
}

function isPaidAndOrganicAggregationMode(mode) {
  const normalized = String(mode ?? "").trim().toLowerCase()
  return normalized === "paid_and_organic" || normalized === "paidandorganic"
}

/** Instagram/Facebook story rows from the posts API — not feed/reel Post Performance content. */
export function isStoryPostRow(row) {
  if (!row || typeof row !== "object") return false
  const postType = row.post_type ?? row.postType
  if (postType == null || postType === "") return false
  return String(postType).trim().toLowerCase() === "story"
}

/** Post performance API rows include organic_only duplicates — keep paid_and_organic only when mode is present. */
export function filterTopPostsPerformanceRows(rows) {
  const list = (Array.isArray(rows) ? rows : []).filter((row) => !isStoryPostRow(row))
  const hasAggregationModes = list.some(
    (row) => row?.aggregation_mode != null || row?.aggregationMode != null,
  )
  if (!hasAggregationModes) return list

  return list.filter((row) =>
    isPaidAndOrganicAggregationMode(row?.aggregation_mode ?? row?.aggregationMode),
  )
}

function pickRowValue(row, ...keys) {
  if (!row || typeof row !== "object") return null
  for (const key of keys) {
    const value = row[key]
    if (value != null && value !== "") return value
  }
  return null
}

export function normalizePostCaptionText(value) {
  return String(value ?? "").replace(/\s+/g, " ").trim()
}

/** Bold overlay title + remaining caption. Uses topic when present, else the first sentence. */
export function splitPostOverlayText(postText, topic) {
  const copy = normalizePostCaptionText(postText)
  const title = normalizePostCaptionText(topic)
  if (title) return { title, copy }
  if (!copy) return { title: "", copy: "" }
  const sentence = copy.match(/^(.{8,88}?[.!?])(?:\s+)(.+)$/)
  if (sentence?.[2]) {
    return { title: sentence[1].trim(), copy: sentence[2].trim() }
  }
  return { title: "", copy }
}

/** Post performance API: metrics live in `results` for paid_and_organic rows only. */
export function getPostMetricBuckets(row) {
  if (!row || typeof row !== "object") return null
  const mode = row.aggregation_mode ?? row.aggregationMode
  if (mode != null && mode !== "") {
    if (!isPaidAndOrganicAggregationMode(mode)) return null
    const results = row.results
    return results && typeof results === "object" ? results : null
  }
  const paidOrganic = row.paid_and_organic ?? row.paidAndOrganic
  if (paidOrganic && typeof paidOrganic === "object" && Object.keys(paidOrganic).length > 0) {
    return paidOrganic
  }
  const results = row.results
  if (results && typeof results === "object") return results
  return null
}

function parseMetricNumber(value) {
  if (value == null || value === "") return null
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

function metricLatestFromBuckets(buckets, ...keys) {
  if (!buckets || typeof buckets !== "object") return null
  for (const key of keys) {
    const entry = buckets[key]
    if (!entry || typeof entry !== "object") continue
    const nested = entry.paid_and_organic ?? entry.paidAndOrganic
    const value =
      entry.latest_value ??
      entry.latestValue ??
      nested?.latest_value ??
      nested?.latestValue
    const num = parseMetricNumber(value)
    if (num != null) return num
  }
  return null
}

function metricEntryFromBuckets(buckets, ...keys) {
  if (!buckets || typeof buckets !== "object") return null
  for (const key of keys) {
    const entry = buckets[key]
    if (!entry || typeof entry !== "object") continue
    const nested = entry.paid_and_organic ?? entry.paidAndOrganic
    if (
      nested &&
      typeof nested === "object" &&
      (nested.latest_value != null ||
        nested.latestValue != null ||
        nested.new_value != null ||
        nested.newValue != null)
    ) {
      return nested
    }
    return entry
  }
  return null
}

/** Top Posts cells: always `results.*.latest_value` from paid_and_organic buckets. */
function resolveNumericMetric(buckets, bucketKeys) {
  const fromBuckets = metricLatestFromBuckets(buckets, ...bucketKeys)
  return fromBuckets != null ? fromBuckets : 0
}

/** Post Performance: API `results.new_engagement_rate.latest_value` only (no FE recalculation). */
function resolveEngagementRate(buckets) {
  const rate = metricLatestFromBuckets(buckets, "new_engagement_rate")
  return rate != null ? rate : 0
}

function resolveEngagementSortValue(buckets) {
  return resolveEngagementRate(buckets)
}

/** Post Performance IMPRESSIONS: `results.impressions.latest_value` only (no reach/video-views fallback). */
function resolveImpressions(buckets) {
  return resolveNumericMetric(buckets, ["impressions"])
}

/** Dashboard platform tab: Facebook uses `facebook`; Instagram uses `instagram_graph` from API. */
export function topPostMatchesPlatformFilter(post, platformKey) {
  if (!platformKey || platformKey === "all") return true
  const raw = String(post?.socialNetwork ?? post?.social_network ?? "")
    .trim()
    .toLowerCase()

  if (platformKey === "facebook") {
    if (raw) return raw === "facebook"
    return post?.platform === "facebook"
  }
  if (platformKey === "instagram") {
    if (raw) return isInstagramGraphDescription(raw)
    return post?.platform === "instagram"
  }

  if (raw) {
    const mapped = platformFromSocialNetworkName(raw)
    if (mapped) return mapped === platformKey
  }
  return post?.platform === platformKey
}

export function formatPostTypeBadge(postType) {
  if (!postType || typeof postType !== "string") return null
  const normalized = postType.trim().toLowerCase()
  if (!normalized) return null
  if (normalized === "image" || normalized === "photo") return "Image"
  if (normalized === "video" || normalized.includes("video")) return "Video"
  if (normalized === "carousel" || normalized === "carousel_album" || normalized === "album") {
    return "Carousel"
  }
  if (normalized === "story") return "Story"
  if (normalized === "text") return "Text"
  if (normalized === "event") return "Event"
  return postType.trim().charAt(0).toUpperCase() + postType.trim().slice(1).toLowerCase()
}

/** API ratio × 100, 2 decimals. Missing → 0.00%. e.g. 0.03647 → "3.65%" */
export function formatTopPostEngagementRate(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "0.00%"
  return `${(Number(value) * 100).toFixed(2)}%`
}

export function getTopPostDateValue(post) {
  if (!post || typeof post !== "object") return null
  return post.date ?? post.post_date ?? post.postDate ?? post.publishedAt ?? null
}

/** Keep posts whose property-timezone calendar day is inside the selected dashboard range. */
export function filterTopPostsByPropertyDateRange(
  posts,
  rangeStart,
  rangeEnd,
  propertyTimezone,
) {
  if (!propertyTimezone || rangeStart == null || rangeEnd == null) {
    return Array.isArray(posts) ? posts : []
  }
  return (Array.isArray(posts) ? posts : []).filter((post) =>
    isCalendarDayInRange(getTopPostDateValue(post), rangeStart, rangeEnd, propertyTimezone),
  )
}

/** Post Performance date label in the property timezone (not browser local). */
export function formatTopPostDisplayDate(postDate, propertyTimezone) {
  const formatted = formatShortDateInTimeZone(postDate, propertyTimezone)
  return formatted || "—"
}

/** Numeric value for Top Posts column sorting. */
export function getTopPostSortValue(post, sortBy) {
  if (!post || typeof post !== "object") return 0
  if (sortBy === "engagement") {
    const rate = post.engagementRate
    if (rate != null && rate !== "" && !Number.isNaN(Number(rate))) {
      return Number(rate)
    }
    return 0
  }
  const val = post[sortBy]
  return val != null && val !== "" && !Number.isNaN(Number(val)) ? Number(val) : 0
}

const POST_PERFORMANCE_METRIC_BUCKET_KEYS = {
  engagement: ["new_engagement_rate"],
  likes: ["post_likes"],
  comments: ["post_comments"],
  shares: ["post_shares"],
  saves: ["post_saves"],
  impressions: ["impressions"],
  postClicks: ["link_clicks"],
}

export function getPostMetricSnapshot(post, metricKey = "engagement") {
  const snap = post?.metricSnapshots?.[metricKey]
  if (snap) return snap
  return { latestValue: null, newValue: null, growth: null }
}

const TOP_POST_VIDEO_EXT = /\.(mp4|mov|webm|m4v)(\?|#|$)/i

/** True when post_media_link is a playable video (e.g. Twitter .mp4), not a thumbnail image. */
export function isTopPostVideoMediaUrl(url) {
  if (!url || typeof url !== "string") return false
  const trimmed = url.trim()
  if (!trimmed) return false
  if (TOP_POST_VIDEO_EXT.test(trimmed)) return true
  try {
    const host = new URL(trimmed).hostname.toLowerCase()
    return host === "video.twimg.com" || host.endsWith(".video.twimg.com")
  } catch {
    return false
  }
}

/** Seek to the first frame so muted video cards show a still instead of black. */
export function topPostVideoPreviewSrc(url) {
  if (!url || typeof url !== "string") return url
  if (url.includes("#")) return url
  return `${url}#t=0.1`
}

export function normalizeTopPostsPayload(data, socialNetworkTypes = []) {
  const rows = filterTopPostsPerformanceRows(unwrapTopPostsPayload(data))
  return rows.map((row, index) => {
    const buckets = getPostMetricBuckets(row)
    const postLink = pickRowValue(row, "postLink", "post_link")
    const socialNetworkTypeId = pickRowValue(
      row,
      "social_network_type_id",
      "socialNetworkTypeId",
    )
    const socialNetworkName = pickRowValue(row, "social_network", "socialNetwork")
    const engagementRate = resolveEngagementRate(buckets)
    const engagement = resolveEngagementSortValue(buckets)
    const mediaRaw = pickRowValue(row, "postMediaLink", "post_media_link")
    const mediaLink =
      mediaRaw != null && String(mediaRaw).trim() !== "" ? String(mediaRaw).trim() : null
    const platform = resolveTopPostPlatform(
      socialNetworkTypeId,
      postLink,
      socialNetworkName,
      socialNetworkTypes,
    )

    const metricSnapshots = Object.fromEntries(
      Object.entries(POST_PERFORMANCE_METRIC_BUCKET_KEYS).map(([metricKey, bucketKeys]) => {
        const entry = metricEntryFromBuckets(buckets, ...bucketKeys)
        const latestValue = parseMetricNumber(entry?.latest_value ?? entry?.latestValue)
        const newValue = parseMetricNumber(entry?.new_value ?? entry?.newValue)
        const growth = parseMetricNumber(entry?.growth)
        return [metricKey, { latestValue, newValue, growth }]
      }),
    )

    return {
      topic: pickRowValue(
        row,
        "topic",
        "topicName",
        "topic_name",
        "content_pillar",
        "contentPillar",
        "campaign",
        "campaignName",
        "campaign_name",
        "postName",
        "post_name",
      ),
      postText: normalizePostCaptionText(
        pickRowValue(row, "post_text", "postText", "caption", "message", "text"),
      ),
      postId: pickRowValue(row, "postId", "post_id") ?? `top-post-${index}`,
      postLink,
      date: pickRowValue(row, "postDate", "post_date") ?? null,
      image: mediaLink,
      isVideoMedia: isTopPostVideoMediaUrl(mediaLink),
      socialNetwork:
        socialNetworkName != null && String(socialNetworkName).trim() !== ""
          ? String(socialNetworkName).trim().toLowerCase()
          : null,
      socialNetworkTypeId:
        socialNetworkTypeId != null && socialNetworkTypeId !== ""
          ? String(socialNetworkTypeId)
          : null,
      platform,
      contentType: formatPostTypeBadge(pickRowValue(row, "postType", "post_type")),
      engagementRate,
      engagement,
      likes: resolveNumericMetric(buckets, ["post_likes"]),
      comments: resolveNumericMetric(buckets, ["post_comments"]),
      shares: resolveNumericMetric(buckets, ["post_shares"]),
      saves: resolveNumericMetric(buckets, ["post_saves"]),
      impressions: resolveImpressions(buckets),
      postClicks: resolveNumericMetric(buckets, ["link_clicks"]),
      engagements: resolveNumericMetric(buckets, ["new_post_engagement"]),
      permalink: postLink,
      videoViews: resolveNumericMetric(buckets, ["post_video_views"]),
      metricSnapshots,
      fromApi: true,
    }
  })
}
